import React, { useCallback, useEffect, useRef, useState } from "react";
import { Theme, withStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import { makeStyles } from "@material-ui/core";
import Button from "../../../components/Button";
import { IWizard, WizardMode, SubmitButtonColor } from "../types";
import clsx from "clsx";
import { DialogTitle } from "./WizardTitle";
import { mutateErrors } from "../../../utils";
import Loading from "../../../components/Loading";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { WIZARD_ERROR_MESSAGE } from "../constants";
import { useNotify } from "../../notify/useNotify";
import { NotifyMode } from "../../notify/NotifyModeEnum";
import { darken } from "@material-ui/core/styles/colorManipulator";

const useStyles = makeStyles((theme: Theme) => ({
  contentWidthScroll: {
    marginRight: -8,
    padding: "0 8px 0 0",
    minHeight: theme.spacing(8),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    fontFamily: "Roboto",
    fontSize: 16,
    fontWeight: "normal",
    letterSpacing: "-0.1px",
    color: "#535358",
    overflowY: "auto",
    overflowX: "hidden",
    maxHeight: theme.spacing(68),

    "&::-webkit-scrollbar-track": {
      borderRadius: 4,
      background: "#e4e4e4",
    },
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "14px",
      background: "#e4e4e4",
      borderRadius: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#acacac",
      border: "2px solid #e4e4e4",
      borderBottomWidth: "2px",
      borderRadius: "20px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#a8a8a8",
    },
  },
  button: ({ isSingleButton }: any) => ({
    fontFamily: "Roboto",
    fontSize: 16,
    fontWeight: "bold",
    maxWidth: !isSingleButton ? theme.spacing(31) : "unset",
    height: theme.spacing(5),
    "&:not(:first-child)": {
      marginLeft: theme.spacing(3),
    },
  }),
  mainButton: {
    color: "#fefefe",
  },
  mainButtonEdit: {
    backgroundColor: theme.palette.secondary.main,
    "&:hover": {
      backgroundColor: "#d9b721",
    },
  },
  mainButtonCreate: {
    backgroundColor: theme.palette.success.main,
    "&:hover": {
      backgroundColor: "#78c07e",
    },
  },
  mainButtonView: {
    backgroundColor: theme.palette.info.main,
    "&:hover": {
      backgroundColor: "#2c78de",
    },
  },
  mainButtonDelete: {
    backgroundColor: theme.palette.error.main,
    "&:hover": {
      backgroundColor: darken(theme.palette.error.main, 0.12),
    },
  },
  secondaryButton: {
    color: "#757a8a",
  },
  dialog: (props: any) => ({
    width: props.dialogWidth,
    padding: theme.spacing(2),
    borderRadius: 16,
    // transition: "width 0.3s ease-out, max-width 0.3s ease-out"
  }),
  dialogTitleForSingleStep: {
    padding: 0,
  },
  dialogTitleForMultipleSteps: {
    margin: theme.spacing(-2, -2, 2),
    padding: 0,
    backgroundColor: "#f5f6f7",
    textAlign: "center",
    height: theme.spacing(7),
  },
  backDrop: {
    backdropFilter: "blur(7px)",
    backgroundColor: "rgba(0,0,30,0.4)",
  },
}));

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function Wizard({
  id,
  resolveCallback,
  submitButtonText = "Submit",
  cancelButtonText = "Close",
  submitButtonColor = SubmitButtonColor.Default,
  steps,
  setStepValidity,
  saveCallback,
  getButtonConfigs,
  deleteInstance,
  mode = WizardMode.Edit,
  initialData,
  showSuccessNotification = true,
}: IWizard) {
  const [submitBtnText, setSubmitBtnText] = useState(submitButtonText);
  const [submitBtnColor, setSubmitBtnColor] = useState(submitButtonColor);
  const [cancelBtnText, setCancelBtnText] = useState(cancelButtonText);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const activeStep = steps[activeStepIndex];
  const StepContent = activeStep.content;
  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === steps.length - 1;

  const activeStepName = activeStep.stepName || "step" + activeStepIndex;

  const dialogClasses = useStyles({
    dialogWidth: activeStep.width || "400px",
    isSingleButton: cancelBtnText === null || submitBtnText === null,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isSubmitting = useRef(false);
  const snackNotify = useNotify();

  let stepSubmitCallback;
  let setFieldData;

  const closeWizard = useCallback(
    (data?: any) => {
      resolveCallback!(data);
      deleteInstance(id);
    },
    [resolveCallback, deleteInstance],
  );

  const initialDataWithMode = { ...initialData, mode, closeWizard };

  const nextClickHandler = async () => {
    if (isLoading || isSubmitting.current) return;

    setFieldData?.("isInnerSubmission", false);

    if (stepSubmitCallback) {
      let result = await stepSubmitCallback();

      let isValid = result === undefined;

      setStepValidity(
        id,
        activeStepIndex,
        result === undefined || result === null,
      );

      if (!isValid) {
        return;
      }
    }

    if (!isLastStep) {
      setActiveStepIndex((currentIndex) => currentIndex + 1);
      return;
    }

    closeWizard(true);
  };

  const backClickHandler = () => {
    if (isLoading) return;
    if (isFirstStep) {
      closeWizard(false);
    } else {
      setActiveStepIndex((currentIndex) => currentIndex - 1);
    }
  };

  const submitHandler = useCallback(
    async (values) => {
      await sleep(1); // makes function asynchronous

      if (activeStep.validator) {
        try {
          const result = await activeStep.validator({
            values,
            activeStepName,
            backendValidator: activeStep.backendValidator,
          });

          if (result?.[WIZARD_ERROR_MESSAGE]) {
            snackNotify(
              "Warning",
              result[WIZARD_ERROR_MESSAGE],
              NotifyMode.WARNING,
            );

            return null;
          }
        } catch (errors) {
          console.error(errors);
          if (errors && errors[WIZARD_ERROR_MESSAGE]) {
            snackNotify(
              "Error",
              errors[WIZARD_ERROR_MESSAGE],
              NotifyMode.ERROR,
            );

            delete errors[WIZARD_ERROR_MESSAGE];
          }

          return errors;
        }
      }

      if (!isLastStep) {
        return;
      }

      if (!saveCallback) {
        return;
      }

      setIsLoading(true);

      try {
        const response = await saveCallback(values);

        showSuccessNotification &&
          response !== null &&
          snackNotify(
            "Success",
            response?.data?.message || "Success",
            NotifyMode.SUCCESS,
          );
        closeWizard(response?.data);
      } catch (e) {
        console.error(e);
        let { errors, message } = mutateErrors(e);

        if (typeof activeStep.errorParser === "function") {
          errors = activeStep.errorParser(errors, values);
        }

        snackNotify("Error", message, NotifyMode.ERROR);
        setIsLoading(false);

        return errors;
      }
    },
    [
      activeStep,
      isLastStep,
      saveCallback,
      activeStepName,
      snackNotify,
      showSuccessNotification,
      closeWizard,
    ],
  );

  let mainButton;

  if (submitBtnColor === SubmitButtonColor.Default) {
    mainButton = dialogClasses.mainButtonEdit;
  } else if (submitBtnColor === SubmitButtonColor.Success) {
    mainButton = dialogClasses.mainButtonCreate;
  } else if (submitBtnColor === SubmitButtonColor.Info) {
    mainButton = dialogClasses.mainButtonView;
  } else if (submitBtnColor === SubmitButtonColor.Danger) {
    mainButton = dialogClasses.mainButtonDelete;
  }

  const FormContent = (props: any) => {
    stepSubmitCallback = props.handleSubmit;
    setFieldData = props.form.change;
    isSubmitting.current = props.submitting;

    useEffect(() => {
      if (typeof getButtonConfigs === "function") {
        const buttonConfigs = getButtonConfigs(props.values);
        setSubmitBtnText(
          buttonConfigs.submitButton?.text
            ? buttonConfigs.submitButton.text
            : null,
        );
        setSubmitBtnColor(
          buttonConfigs.submitButton?.color
            ? buttonConfigs.submitButton.color
            : null,
        );
        setCancelBtnText(
          buttonConfigs.cancelButton?.text
            ? buttonConfigs.cancelButton.text
            : null,
        );
      }
    }, [props.values]);

    return typeof StepContent === "function" ? (
      <StepContent {...props} stepName={activeStepName} />
    ) : (
      StepContent
    );
  };

  return (
    <Dialog
      disableBackdropClick
      onClose={() => {
        closeWizard(null);
      }}
      aria-labelledby="customized-wizard-title"
      open={true}
      classes={{ paper: dialogClasses.dialog }}
      maxWidth={false}
      scroll="body"
      BackdropProps={{
        classes: {
          root: dialogClasses.backDrop,
        },
      }}
    >
      {isLoading && <Loading />}
      <DialogTitle
        id="customized-wizard-title"
        onClose={() => {
          closeWizard(null);
        }}
        classes={{
          root:
            steps.length === 1
              ? dialogClasses.dialogTitleForSingleStep
              : dialogClasses.dialogTitleForMultipleSteps,
        }}
        activeStepIndex={activeStepIndex}
        steps={steps}
      />
      <DialogContent className={dialogClasses.contentWidthScroll}>
        <Form
          onSubmit={submitHandler}
          initialValues={initialDataWithMode}
          mutators={{
            callMe: ([dispatch], state, utils) => {
              dispatch(state, utils);
            },
            ...arrayMutators,
          }}
          render={FormContent}
        />
      </DialogContent>
      <DialogActions>
        {cancelBtnText !== null && (
          <Button
            fullWidth
            color="default"
            onClick={backClickHandler}
            variant="contained"
            className={clsx(
              dialogClasses.button,
              dialogClasses.secondaryButton,
            )}
          >
            {activeStepIndex === 0 ? cancelBtnText : "Back"}
          </Button>
        )}
        {submitBtnText !== null && (
          <Button
            fullWidth
            color="secondary"
            onClick={nextClickHandler}
            variant="contained"
            className={clsx(
              dialogClasses.button,
              activeStepIndex === steps.length - 1
                ? mainButton
                : dialogClasses.mainButtonEdit,
            )}
          >
            {activeStepIndex === steps.length - 1 ? submitBtnText : "Next"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

const DialogActions = withStyles(() => ({
  root: {
    margin: 0,
    padding: 0,
  },
}))(MuiDialogActions);
